import { useEffect, useState, useCallback } from 'react';
import {
  NavLink,
  generatePath,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  PermissionEnum,
  responseErrorsResolver,
  ResponseError,
  IconChangeLoClock,
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  ChangeLogs,
  PermissionDenied,
  IconCloseCircle,
  Textarea,
  Popup,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import SummaryInfo from '../../../components/partial/summary-info/summary-info';
import GeneralInfo from '../../../components/corporate/overview/general';
import CompanyOfficial from '../../../components/corporate/overview/company-official';
import TopUpOptions from '../../../components/partial/overview-topup/overview-topup';
import TopUpPanel from '../../customers/top-up-panel/top-up-panel';
// import OverviewRecentActivity from './overview-recent-acitivty';
// import TransactionSigningRules from '../../../components/corporate/overview/transaction-signing-rules';
import CustomerComments from '../../../components/partial/comments/comments';
import OverviewStatements from '../../customers/statements/statements';
import {
  CustomerProps,
  CustomerStatus,
  CustomerType,
} from '../../customers/types';
import { setTopUpIds } from '../store/slices/general-info';
import { CorporateOverviewGeneral } from '../types';
import {
  useUpdateCorporateInfoMutation,
  useLazyGetCorporateCOFQuery,
} from '../store/services/corporate';
import {
  useLazyGetCustomersTopUpQuery,
  useLazyGetCustomersChangeLogsQuery,
  useReopenOrganizationMutation,
  useLazyGetAllTopUpVisibilityQuery,
  useLazyGetCustomerCommentsQuery,
  useAddCommentToCustomerMutation,
  useEditCommentInCustomerMutation,
  useDeleteCommentInCustomerMutation,
} from '../../customers/store/services/customers';

import ROUTES from '../../../routes';

import cn from '../../individual/information.module.css';

const CorporateCustomerOverview = (): React.JSX.Element => {
  const {
    corporateCustomers: {
      info: {
        general: {
          baseInformation,
          baseInformationIsLoading,
          totalBalance,
          topUpOptions,
          topUpIsLoading,
          balances,
          topUpOptionsPagination,
          changeLogs,
          changeLogsPagination,
          topUpVisibility,
          topUpIds,
          customerComments,
          customerCommentsPagination,
        },
        overview: { companyOfficial, baseInfo },
      },
    },
    customersDictionaries: { cardsEnabled },
    root: { activePermissions },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [topUpPanelIsOpen, setTopUpPanelIsOpen] = useState(false);
  const [selectedTopUpId, setSelectedTopUpId] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [changeLogsPanelIsOpen, setChangeLogPanelIsOpen] = useState(false);
  const [reopenPopupVisible, setReopenPopupVisible] = useState(false);
  const [reopenPopupState, setReopenPopupState] = useState<
    'warning' | 'comment'
  >('warning');
  const [reopenReason, setReopenReason] = useState('');
  const [reopenError, setReopenError] = useState('');

  const [getCorporateCof, { isLoading: cofIsLoading }] =
    useLazyGetCorporateCOFQuery();

  const [
    getTopUp,
    { isLoading: topUpOptionsIsLoading, isFetching: topUpOptionsIsFetching },
  ] = useLazyGetCustomersTopUpQuery();
  const [
    getAllTopUpVisibility,
    {
      isLoading: topUpVisibilityIsLoading,
      isFetching: topUpVisibilityIsFetching,
    },
  ] = useLazyGetAllTopUpVisibilityQuery();

  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetCustomersChangeLogsQuery();

  const [
    reopenOrganization,
    { isLoading: reopenInProgress, isSuccess: reopenIsSuccess },
  ] = useReopenOrganizationMutation();

  const [
    getCustomerComments,
    { isLoading: commentsIsLoading, isFetching: commentsIsFetching },
  ] = useLazyGetCustomerCommentsQuery();
  const [
    addCommentToCustomer,
    { isLoading: addCommentInProgress, isSuccess: addCommentIsSuccess },
  ] = useAddCommentToCustomerMutation();
  const [
    editCommentInCustomer,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditCommentInCustomerMutation();
  const [
    deleteCommentInCustomer,
    { isLoading: deleteCommentInProgress, isSuccess: deleteCommentIsSuccess },
  ] = useDeleteCommentInCustomerMutation();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    setError,
  } = useForm<CorporateOverviewGeneral>();

  const correspondenceAddressSameAsRegisteredAddress = watch(
    'correspondenceAddressSameAsRegisteredAddress',
  );
  const businessAddressSameAsRegisteredAddress = watch(
    'businessAddressSameAsRegisteredAddress',
  );

  const { customerId } = useParams();

  const [updateCorporateInfo, { isLoading, isSuccess }] =
    useUpdateCorporateInfoMutation();

  const getAllTopUpVisibilityAction = useCallback(
    async (query: string) => {
      await getAllTopUpVisibility({
        organizationId: customerId as string,
        query,
      });
    },
    [customerId],
  );

  const onReopenOrganization = async () => {
    setReopenError('');

    const response = (await reopenOrganization({
      id: customerId as string,
      body: {
        statusChangeReason: reopenReason,
      },
    })) as { error: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setReopenError,
      });
    }
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=ORGANIZATION&entityId=${customerId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [customerId],
  );

  const getNextTopUpOptions = (cursor: string) => {
    getTopUp({ cursor });
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const body: Partial<CorporateOverviewGeneral> = {
      registeredCountryId: values.registeredCountryId,
      registeredCity: values.registeredCity,
      registeredPostalCode: values.registeredPostalCode,
      registeredAddress1: values.registeredAddress1,
      registeredAddress2: values.registeredAddress2,
      companyNumber: values.companyNumber,
      dateOfIncorporation: values.dateOfIncorporation,
      countryOfIncorporationId: values.countryOfIncorporationId,
      websiteLink: values.websiteLink,
      sicCodes: values.sicCodes,
      companyName: values.companyName,
      productIds: values.productIds,
      merchantId: values?.merchantId || '',
    };

    if (values.businessDescription) {
      body.businessDescription = values.businessDescription;
    }

    if (values.vatNumber) {
      body.vatNumber = values.vatNumber;
    }

    // if (values.mccCode && values.mccCode !== '-') {
    //   body.mccCode = values.mccCode;
    // }

    if (!correspondenceAddressSameAsRegisteredAddress) {
      if (values.correspondenceCountryId) {
        body.correspondenceCountryId = values.correspondenceCountryId;
      }

      if (values.correspondencePostalCode) {
        body.correspondencePostalCode = values.correspondencePostalCode;
      }

      if (values.correspondenceCity) {
        body.correspondenceCity = values.correspondenceCity;
      }

      if (values.correspondenceAddress1) {
        body.correspondenceAddress1 = values.correspondenceAddress1;
      }

      if (values.correspondenceAddress2) {
        body.correspondenceAddress2 = values.correspondenceAddress2;
      }
    }

    if (!businessAddressSameAsRegisteredAddress) {
      if (values.businessCountryId) {
        body.businessCountryId = values.businessCountryId;
      }

      if (values.businessCity) {
        body.businessCity = values.businessCity;
      }

      if (values.businessPostalCode) {
        body.businessPostalCode = values.businessPostalCode;
      }

      if (values.businessAddress1) {
        body.businessAddress1 = values.businessAddress1;
      }

      if (values.businessAddress2) {
        body.businessAddress2 = values.businessAddress2;
      }
    }

    const response = (await updateCorporateInfo({
      id: customerId as string,
      body,
    })) as {
      data?: { data: CustomerProps };
      error?: ResponseError;
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  const match = useMatch(ROUTES.CUSTOMERS_CORPORATE_OVERVIEW);
  const cardsMatch = useMatch(
    generatePath(ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_CARDS, {
      customerId: customerId as string,
    }),
  );

  const navigate = useNavigate();

  const onAddCommentToCustomer = async (text: string) => {
    await addCommentToCustomer({
      id: customerId as string,
      body: {
        text,
      },
    });
  };

  const onEditCommentInCustomer = async ({
    text,
    commentId,
  }: {
    text: string;
    commentId: string;
  }) => {
    await editCommentInCustomer({
      id: customerId as string,
      commentId,
      body: {
        text,
      },
    });
  };

  const onDeleteCommentInCustomer = async (commentId: string) => {
    await deleteCommentInCustomer({
      id: customerId as string,
      commentId,
    });
  };

  const getCustomerCommentsAction = async (cursor?: string) => {
    await getCustomerComments({
      id: customerId as string,
      cursor: cursor || '',
    });
  };

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_COMMENTS_LIST,
      )
    ) {
      getCustomerCommentsAction();
    }
  }, []);

  useEffect(() => {
    if (customerId && match) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_TRANSACTIONS, {
          customerId,
        }),
      );
    }
  }, [customerId, match]);

  useEffect(() => {
    if (customerId && topUpOptions) {
      const nextTopUpIds = ([] as string[]).concat(topUpIds as string[]);

      const topUpIdsValues = topUpOptions?.map((item) => item.id);

      topUpIdsValues?.forEach((item) => {
        if (!nextTopUpIds?.find((id) => item === id)) {
          nextTopUpIds.push(item);
        }
      });

      if (
        topUpIdsValues &&
        topUpIdsValues?.filter((item) => topUpIds?.every((id) => id !== item))
          ?.length
      ) {
        const query = `&pspPartnerId=${topUpIdsValues
          ?.filter((item) => topUpIds?.every((id) => id !== item))
          ?.join('&pspPartnerId=')}`;

        getAllTopUpVisibilityAction(query);
        dispatch(setTopUpIds(nextTopUpIds));
      }
    }
  }, [customerId, topUpOptions, topUpIds]);

  useEffect(() => {
    if (customerId) {
      getCorporateCof(customerId);
    }
  }, [customerId]);

  useEffect(() => {
    if (
      changeLogsPanelIsOpen &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [changeLogsPanelIsOpen]);

  useEffect(() => {
    let title = baseInformation?.name
      ? `${baseInformation?.name}: Overview`
      : 'Overview';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation?.name]);

  useEffect(() => {
    if (baseInfo) {
      reset(baseInfo);
    }
  }, [baseInfo]);

  useEffect(() => {
    if (reopenIsSuccess) {
      setReopenError('');
      setReopenPopupState('warning');
      setReopenPopupVisible(false);
    }
  }, [reopenIsSuccess]);

  return (
    <div className={cn.customerContent}>
      <div
        className={`${cn.overviewTitleWrapper} ${
          !baseInformation?.name ? cn.flexEnd : ''
        }`}
      >
        {baseInformation?.name && (
          <div className={cn.overviewTitle}>{baseInformation.name}</div>
        )}

        <div className={cn.overviewTopRight}>
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <button
              className={cn.applicationLink}
              type="button"
              onClick={() => setChangeLogPanelIsOpen(true)}
            >
              <IconChangeLoClock />
              Change Log
            </button>
          )}
          {baseInformation?.applicationId && (
            <NavLink
              to={generatePath(
                ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_INFO,
                {
                  applicationId: baseInformation?.applicationId || '',
                },
              )}
              className={cn.applicationLink}
              target="_blank"
            >
              Go to application
            </NavLink>
          )}
        </div>
      </div>
      {baseInformation?.status === CustomerStatus.CLOSED && (
        <div className={cn.closedContent}>
          <div className={cn.closeContentLeft}>
            <div className={cn.closeContentLeftTitle}>
              <IconCloseCircle width={24} height={24} />
              Organization is closed
            </div>
            <div className={cn.closeContentLeftDescription}>
              {baseInformation?.statusChangeReason}
            </div>
          </div>
          {activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_OPEN_ACTION,
          ) && (
            <div className={cn.closeContentRight}>
              <button
                type="button"
                className={cn.reopenButton}
                onClick={() => setReopenPopupVisible(true)}
              >
                Reopen
              </button>
            </div>
          )}
        </div>
      )}
      <SummaryInfo
        accounts={baseInformation?.accounts?.length as number}
        currencies={totalBalance?.currency as string[]}
        balance={totalBalance?.balance as number}
        cardsEnabled={cardsEnabled}
        balances={balances}
      />
      <GeneralInfo
        isLoading={baseInformationIsLoading as boolean}
        general={baseInfo as CorporateOverviewGeneral}
        control={control}
        correspondenceAddressSameAsRegisteredAddress={
          correspondenceAddressSameAsRegisteredAddress
        }
        businessAddressSameAsRegisteredAddress={
          businessAddressSameAsRegisteredAddress
        }
        onSubmit={onSubmit}
        requestInProgress={isLoading}
        isSuccess={isSuccess}
        errors={errors}
        globalError={globalError}
        status={baseInformation?.status}
      />
      {activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_COMMENTS_LIST,
      ) && (
        <CustomerComments
          comments={customerComments}
          pagination={customerCommentsPagination}
          isLoading={commentsIsLoading}
          isFetching={commentsIsFetching}
          getCommentsList={getCustomerCommentsAction}
          createAvailable={activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_COMMENTS_CREATE_ACTION,
          )}
          editAvailable={activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_COMMENTS_EDIT_ACTION,
          )}
          deleteAvailable={activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_COMMENTS_DELETE_ACTION,
          )}
          onAddCommentToCustomer={onAddCommentToCustomer}
          addCommentInProgress={addCommentInProgress}
          addCommentIsSuccess={addCommentIsSuccess}
          onEditCommentInCustomer={onEditCommentInCustomer}
          editCommentInProgress={editCommentInProgress}
          editCommentIsSuccess={editCommentIsSuccess}
          users={users}
          onDeleteCommentInCustomer={onDeleteCommentInCustomer}
          deleteCommentInProgress={deleteCommentInProgress}
          deleteCommentIsSuccess={deleteCommentIsSuccess}
          changeLogsAvailable={activePermissions?.includes(
            PermissionEnum.AUDIT_LOG_LIST,
          )}
        />
      )}
      <CompanyOfficial
        isLoading={cofIsLoading}
        companyOfficial={companyOfficial}
        customerId={customerId as string}
        cardsMatch={Boolean(cardsMatch)}
        status={baseInformation?.status}
      />
      {/* <TransactionSigningRules
        transactionSigningRulesList={transactionSigningRule.rulesList}
        transactionSigningRule={transactionSigningRule.value}
        defaultTransactionSigningRule={transactionSigningRule.defaultValue}
        isLoading={transactionSigningRule.isLoading}
        requestInProgress={transactionSigningRule.requestInProgress}
        onSubmit={() => dispatch(onSubmitRule())}
        onChange={(value) => dispatch(onChangeRule(value))}
      /> */}
      <TopUpOptions
        topUpOptions={topUpOptions?.filter((item) => item.visibleToClients)}
        isLoading={topUpIsLoading}
        customerType={CustomerType.CORPORATE}
        organizationId={customerId}
        setTopUpPanelIsOpen={setTopUpPanelIsOpen}
        setSelectedTopUpId={setSelectedTopUpId}
        topUpOptionsPagination={topUpOptionsPagination}
        getNextTopUpOptions={getNextTopUpOptions}
        topUpOptionsIsLoading={topUpOptionsIsLoading || topUpOptionsIsFetching}
        status={baseInformation?.status}
        topUpVisibility={topUpVisibility}
        topUpVisibilityIsLoading={
          topUpVisibilityIsLoading || topUpVisibilityIsFetching
        }
      />
      <TopUpPanel
        topUpPanelIsOpen={topUpPanelIsOpen}
        setTopUpPanelIsOpen={setTopUpPanelIsOpen}
        selectedTopUpId={selectedTopUpId}
        setSelectedTopUpId={setSelectedTopUpId}
        userAccounts={baseInformation?.accounts?.map((item) => ({
          accountId: item.id,
          account: { fullNumber: item.fullNumber },
          description: item.description,
        }))}
        type={CustomerType.CORPORATE}
      />
      {/* <OverviewRecentActivity /> */}
      {activePermissions?.includes(
        PermissionEnum.CUSTOMERS_STATEMENTS_LIST,
      ) && (
        <OverviewStatements
          matchPath={ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_TRANSACTIONS}
          cardsRoute={ROUTES.CUSTOMERS_CORPORATE_CARDS}
          routes={{
            transationRoute: ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_TRANSACTIONS,
            cardsRoute: ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_CARDS,
          }}
          accounts={
            baseInformation?.id === customerId
              ? baseInformation?.accounts
              : undefined
          }
          cards={baseInformation?.cards as string[]}
          cardsEnabled={cardsEnabled}
          balances={balances}
        />
      )}
      <Popup
        title={
          reopenPopupState === 'warning'
            ? 'Reopen organization'
            : 'State the reason for reopening'
        }
        description={
          reopenPopupState === 'warning'
            ? 'The organization will become available again'
            : ''
        }
        onCancelClick={() => {
          setReopenPopupVisible(false);
          setReopenPopupState('warning');
          setReopenError('');
        }}
        okButtonText={reopenInProgress ? 'Loading...' : 'Reopen organization'}
        cancelButtonText="Cancel"
        isVisible={reopenPopupVisible}
        isLoading={reopenInProgress}
        okButtonDisabled={
          Boolean(reopenPopupState === 'comment' && !reopenReason) ||
          reopenInProgress
        }
        onOkClick={() => {
          if (reopenPopupState === 'warning') {
            setReopenPopupState('comment');
          } else if (reopenPopupState === 'comment') {
            onReopenOrganization();
          }
        }}
      >
        <div>
          {reopenPopupState === 'comment' && (
            <>
              {reopenError && (
                <div className={cn.reopenError}>{reopenError}</div>
              )}
              <Textarea
                name="reopenReason"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Reason"
                labelClassName={cn.reopenReason}
              />
            </>
          )}
        </div>
      </Popup>
      <SidePanel
        isOpen={changeLogsPanelIsOpen}
        onClose={() => {
          setChangeLogPanelIsOpen(false);
        }}
        title="Customer change logs"
        type={SidePanelType.Accounts}
        sidepanelExpandedKey="customers-change-logs"
        footer={
          <div>
            <ButtonPseudo
              text="Close"
              onClick={() => setChangeLogPanelIsOpen(false)}
              bold
            />
          </div>
        }
        closeOnClickOutside
      >
        <>
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <ChangeLogs
              entityType="ORGANIZATION"
              entityId={customerId as string}
              changeLogsIsLoading={changeLogsIsLoading}
              changeLogs={changeLogs}
              activePermission={activePermissions}
              users={users}
              pagination={changeLogsPagination}
              changeLogsIsFetching={changeLogsIsFetching}
              getChangeLogs={getChangeLogsAction}
            />
          )}
          {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <PermissionDenied />
          )}
        </>
      </SidePanel>

      <div id="popup-access" />
    </div>
  );
};

export default CorporateCustomerOverview;
